<template>
  <div class="min-h-screen bg-gray-50 py-8">
    <Breadcrumbs class="px-4 sm:px-6" />
    <!-- Main Content -->
    <div class="max-w-[1400px] mx-auto p-4 sm:p-6">
      <div class="flex flex-col gap-4 lg:flex-row lg:gap-8">
        <!-- Left Column - Main Content -->
        <div class="flex-1">
          <!-- Back Button -->
          <button
            class="flex items-center gap-2 text-gray-600 hover:text-brand mb-4 print:hidden
            cursor-pointer"
            @click="goBack"
          >
            <ArrowLeft :size="16" />
            {{ t(navStore.backButtonLabel) }}
          </button>

          <ImageGallery :images="images" :deal-name="displayName" :on-image-load="onImageLoad" />

          <!-- Property Title & Actions -->
          <PropertyHeader :deal-id="deal.id" :display-name="displayName" :address="deal.building.address">
            <PropertySummary :deal="deal" :currencies="pageContext.data.currencies">
              <!-- Disclaimer -->
              <div class="mt-6 pt-6 border-t border-gray-100 text-xs text-gray-500 leading-relaxed">
                {{ t('property.disclaimer') }}
              </div>
            </PropertySummary>
          </PropertyHeader>

          <!-- Description -->
          <div v-if="page.description" class="bg-white rounded-2xl p-6 mb-6">
            <h2 class="text-xl font-bold text-gray-900 mb-4">{{ t('property.description') }}</h2>
            <p class="text-gray-700 leading-relaxed whitespace-pre-line">
              {{ page.description }}
            </p>
          </div>

          <PropertyFeatures :labels="deal.labels" />

          <!-- Neighborhood -->
          <div class="bg-white rounded-2xl p-6 mb-7">
            <div class="flex items-center justify-between mb-6">
              <h2 class="text-xl font-bold text-gray-900">{{ t('property.transit') }}</h2>
            </div>

            <!-- Stations -->
            <div>
              <div class="flex items-center gap-2 mb-3">
                <Train :size="20" class="text-brand" />
                <h3 class="font-semibold text-gray-900">{{ t('property.nearby_stations') }}</h3>
                <Tooltip position="top">
                  <template #trigger>
                    <HelpCircle :size="16" class="text-gray-500 cursor-pointer" />
                  </template>
                  <template #content>
                    <p>{{ t('property.copyright.mlit', { url: mlitDataSourceUrls.stations }) }}</p>
                  </template>
                </Tooltip>
              </div>
              <div class="space-y-2">
                <div
                  v-for="(station, index) in nearbyStations"
                  :key="index"
                  class="flex items-center justify-between p-3 bg-gray-50 rounded-2xl"
                >
                  <div>
                    <div class="font-semibold text-gray-900">
                      <a
                        :href="getGoogleMapsSearchUrlByName(station.name)" target="_blank"
                        rel="noopener noreferrer" class="inline-flex items-center gap-1.5 hover:underline"
                      >
                        {{ station.name }} <ExternalLink :size="12" />
                      </a>
                    </div>
                    <div class="text-sm text-gray-600">{{ station.line }}</div>
                  </div>
                  <div class="text-sm font-semibold text-brand">
                    {{ t('property.min_walk', { min: station.walkTime }) }}
                  </div>
                </div>
              </div>
            </div>
          </div>

          <!-- Other Units in Building -->
          <div v-if="otherUnits.length" class="bg-white rounded-2xl p-6 mb-6">
            <h2 class="text-xl font-bold text-gray-900 mb-4">
              {{ t('property.other_units') }}
            </h2>
            <div class="space-y-3">
              <PropertyCard
                v-for="unit in otherUnits"
                :key="unit.id"
                direction="row"
                :deals="[unit]"
                :agents="agents"
              />
            </div>
          </div>

          <PropertyMap />

          <!-- Neighborhood -->
          <div class="bg-white rounded-2xl p-6 mb-7">
            <div class="flex items-center justify-between mb-6">
              <h2 class="text-xl font-bold text-gray-900">{{ t('property.neighborhood') }}</h2>
              <a
                v-if="page?.data?.openMarketPath"
                :href="`https://market.openrm.jp/${page.data.openMarketPath}`"
                target="_blank"
                rel="noopener noreferrer"
                class="inline-flex items-center gap-1 px-3 py-1.5 text-xs font-medium text-gray-700 bg-gray-100 border border-gray-200 rounded-full hover:bg-gray-200 transition-colors"
              >
                <img src="/assets/favicon.svg" class="w-3.5 h-3.5" alt="" />
                {{ t('property.view_livability_data') }}
              </a>
            </div>

            <!-- Schools -->
            <div class="mb-7">
              <div class="flex items-center gap-2 mb-3">
                <GraduationCap :size="20" class="text-brand" />
                <h3 class="font-semibold text-gray-900">{{ t('property.nearby_schools') }}</h3>
                <Tooltip position="top">
                  <template #trigger>
                    <HelpCircle :size="16" class="text-gray-500 cursor-pointer" />
                  </template>
                  <template #content>
                    <p>{{ t('property.copyright.mlit', { url: mlitDataSourceUrls.schools }) }}</p>
                  </template>
                </Tooltip>
              </div>
              <div class="space-y-2">
                <div
                  v-for="(school, index) in nearbySchools"
                  :key="index"
                  class="flex items-center justify-between p-3 bg-gray-50 rounded-2xl"
                >
                  <div>
                    <div class="font-semibold text-gray-900">
                      <a
                        :href="getGoogleMapsSearchUrlByName(school.name)" target="_blank"
                        rel="noopener noreferrer" class="inline-flex items-center gap-1.5 hover:underline"
                      >
                        {{ school.name }} <ExternalLink :size="12" />
                      </a>
                    </div>
                    <div class="text-sm text-gray-600">{{ school.type }}</div>
                  </div>
                  <div class="text-sm font-semibold text-brand">
                    {{ school.distance }} km
                  </div>
                </div>
              </div>
            </div>

            <!-- Area Information -->
            <div class="mb-7">
              <div class="flex items-center gap-2 mb-3">
                <Building2 :size="20" class="text-brand" />
                <h3 class="font-semibold text-gray-900">{{ t('property.area_information') }}</h3>
                <Tooltip position="top">
                  <template #trigger>
                    <HelpCircle :size="16" class="text-gray-500 cursor-pointer" />
                  </template>
                  <template #content>
                    <p>{{ t('property.copyright.mlit', { url: mlitDataSourceUrls.urbanPlans }) }}</p>
                  </template>
                </Tooltip>
              </div>
              <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                <div class="p-3 bg-gray-50 rounded-2xl">
                  <div class="text-xs text-gray-600">{{ t('property.schoolDistrict') }} ({{ t('enums.schoolType.elementary') }})</div>
                  <a
                    v-if="elementarySchoolZoneForProperty?.A27_004"
                    class="inline-flex font-semibold items-center gap-1.5 hover:underline"
                    :href="getGoogleMapsSearchUrlByName(elementarySchoolZoneForProperty?.A27_004)"
                    target="_blank"
                  >
                    {{ elementarySchoolZoneForProperty?.A27_004 || 'N/A' }} <ExternalLink :size="12" />
                  </a>
                  <div v-else class="font-semibold">N/A</div>
                </div>
                <div class="p-3 bg-gray-50 rounded-2xl">
                  <div class="text-xs text-gray-600">{{ t('property.schoolDistrict') }} ({{ t('enums.schoolType.juniorHigh') }})</div>
                  <a
                    v-if="juniorHighSchoolZoneForProperty?.A32_004"
                    class="inline-flex font-semibold items-center gap-1.5 hover:underline"
                    :href="getGoogleMapsSearchUrlByName(juniorHighSchoolZoneForProperty?.A32_004)"
                    target="_blank"
                  >
                    {{ juniorHighSchoolZoneForProperty?.A32_004 || 'N/A' }} <ExternalLink :size="12" />
                  </a>
                  <div v-else class="font-semibold">N/A</div>
                </div>
                <div class="p-3 bg-gray-50 rounded-2xl">
                  <div class="text-xs text-gray-600">{{ t('property.usageZone') }}</div>
                  <div class="font-semibold">{{ urbanPlanning.usageZone?.YoutoName || urbanPlanning.usageZone?.AreaName || 'N/A' }}</div>
                </div>
                <div class="p-3 bg-gray-50 rounded-2xl">
                  <div class="text-xs text-gray-600">{{ t('property.cityPlanningArea') }}</div>
                  <div class="font-semibold">{{ urbanPlanning.cityPlanningArea?.TokeiName?.trim() || urbanPlanning.cityPlanningArea?.AreaName || 'N/A' }}</div>
                </div>
                <div class="p-3 bg-gray-50 rounded-2xl">
                  <div class="text-xs text-gray-600">{{ t('property.areaClassification') }}</div>
                  <div class="font-semibold">{{ urbanPlanning.areaClassification?.AreaType || urbanPlanning.areaClassification?.AreaName || 'N/A' }}</div>
                </div>
                <div class="p-3 bg-gray-50 rounded-2xl">
                  <div class="text-xs text-gray-600">{{ t('property.locationOptimizationPlan') }}</div>
                  <div class="font-semibold">{{ urbanPlanning.locationOptimization?.AreaType || urbanPlanning.locationOptimization?.AreaName || 'N/A' }}</div>
                </div>
                <div class="p-3 bg-gray-50 rounded-2xl">
                  <div class="text-xs text-gray-600">{{ t('property.cityPlanningRoad') }}</div>
                  <div class="font-semibold">{{ urbanPlanning.cityPlanningRoad?.DouroName || urbanPlanning.cityPlanningRoad?.AreaName || 'N/A' }}</div>
                </div>
              </div>
            </div>

            <!-- Land Price History -->
            <div class="mb-7">
              <div class="flex items-center gap-2 mb-3">
                <DollarSign :size="20" class="text-brand" />
                <h3 class="font-semibold text-gray-900">{{ t('property.land_price_history') }}</h3>
                <Tooltip position="top">
                  <template #trigger>
                    <HelpCircle :size="16" class="text-gray-500 cursor-pointer" />
                  </template>
                  <template #content>
                    <p>{{ t('property.copyright.mlit', { url: mlitDataSourceUrls.landPriceHistory }) }}</p>
                  </template>
                </Tooltip>
              </div>
              <div class="overflow-x-auto">
                <table class="min-w-full bg-white">
                  <thead class="bg-gray-50">
                    <tr>
                      <th scope="col" class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">{{ t('property.built').split(' ')[0] === 'Built' ? 'Year' : '年' }}</th>
                      <th scope="col" class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">{{ t('property.price_per_size') }}</th>
                      <th scope="col" class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">{{ t('property.total_units') }}</th>
                    </tr>
                  </thead>
                  <tbody class="divide-y divide-gray-200">
                    <tr v-for="item in displayedLandPriceHistory" :key="item.year">
                      <td class="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">{{ item.year }}</td>
                      <td
                        class="px-6 py-4 whitespace-nowrap text-sm text-gray-900 gap-1 group
                        cursor-pointer"
                      >
                        <span class="relative">
                          {{ formatPrice(item.averagePrice) }}
                          <div
                            class="bg-white rounded-2xl p-3 shadow-lg text-gray-600 absolute w-80
                                       whitespace-normal ml-3
                            left-full bottom-0 translate-y-3 opacity-0 group-hover:opacity-100 transition-all duration-300"
                          >
                            Prices:<br>
                            {{ item.prices.map(price => formatPrice(price)).join(' / ') }}
                          </div>
                        </span>
                      </td>
                      <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{{ item.dataPoints }}</td>
                    </tr>
                  </tbody>
                </table>
              </div>
              <button
                v-if="landPriceHistory.length > 3"
                class="mt-2 w-full text-center py-2 px-4 cursor-pointer border border-gray-200 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-brand focus:ring-offset-2"
                @click="showAllLandPriceHistory = !showAllLandPriceHistory"
              >
                {{ showAllLandPriceHistory ? t('property.show_less') : t('property.show_more') }}
              </button>
            </div>
          </div>

          <!-- Documents -->
          <div v-if="false" class="bg-white rounded-2xl p-6 mb-6">
            <h2 class="text-xl font-bold text-gray-900 mb-4">
              Documents & Downloads
            </h2>
            <div class="grid grid-cols-2 gap-3">
              <button class="flex items-center gap-3 p-4 border border-gray-200 rounded-2xl hover:border-brand hover:bg-gray-50 transition-colors">
                <Download :size="20" class="w-5 h-5 text-brand" />
                <div class="text-left">
                  <div class="font-semibold text-gray-900">Floor Plan</div>
                  <div class="text-xs text-gray-600">PDF • 2.4 MB</div>
                </div>
              </button>
              <button class="flex items-center gap-3 p-4 border border-gray-200 rounded-2xl hover:border-brand hover:bg-gray-50 transition-colors">
                <Download :size="20" class="w-5 h-5 text-brand" />
                <div class="text-left">
                  <div class="font-semibold text-gray-900">Property Brochure</div>
                  <div class="text-xs text-gray-600">PDF • 5.1 MB</div>
                </div>
              </button>
              <button class="flex items-center gap-3 p-4 border border-gray-200 rounded-2xl hover:border-brand hover:bg-gray-50 transition-colors">
                <Download :size="20" class="w-5 h-5 text-brand" />
                <div class="text-left">
                  <div class="font-semibold text-gray-900">Building Inspection</div>
                  <div class="text-xs text-gray-600">PDF • 3.8 MB</div>
                </div>
              </button>
              <button class="flex items-center gap-3 p-4 border border-gray-200 rounded-2xl hover:border-brand hover:bg-gray-50 transition-colors">
                <Download :size="20" class="w-5 h-5 text-brand" />
                <div class="text-left">
                  <div class="font-semibold text-gray-900">Management Rules</div>
                  <div class="text-xs text-gray-600">PDF • 1.2 MB</div>
                </div>
              </button>
            </div>
          </div>

          <!-- Property Details -->
          <div class="bg-white rounded-2xl p-6 mb-6">
            <h2 class="text-xl font-bold text-gray-900 mb-6">{{ t('property.details') }}</h2>
            <PropertyDetailsTable
              :deal="deal"
              :currencies="pageContext.data.currencies"
            />
          </div>

          <!-- Similar Properties -->
          <div v-if="nearbyProperties.length" class="bg-white rounded-2xl p-6 print:hidden">
            <h2 class="text-xl font-bold text-gray-900 mb-6">
              {{ t('property.nearby_properties') }}
            </h2>
            <div class="grid grid-cols-1 md:grid-cols-3 gap-4">
              <PropertyCard
                v-for="unit in nearbyProperties"
                :key="unit.id"
                v-bind="unit"
                :agents="agents"
              />
            </div>
          </div>
        </div>

        <!-- Right Column - Sticky Contact Card -->
        <div ref="contactSection" class="w-full md:w-[390px]">
          <InquiryForm
            class="sticky top-26 md:max-h-[calc(100vh-120px)] overflow-y-auto rounded-2xl custom-scrollbar shadow-lg"
            :deal="deal"
            :agents="agents"
            app="openroom"
          />
        </div>
      </div>
    </div>

    <!-- Mobile Floating Bottom Bar -->
    <Transition
      enter-active-class="transition duration-300 ease-out"
      enter-from-class="translate-y-full"
      enter-to-class="translate-y-0"
      leave-active-class="transition duration-200 ease-in"
      leave-from-class="translate-y-0"
      leave-to-class="translate-y-full"
    >
      <div
        v-if="!isContactVisible"
        class="fixed bottom-0 left-0 right-0 bg-white border-t border-gray-200 p-4 shadow-[0_-4px_10px_rgba(0,0,0,0.05)] z-40 lg:hidden"
      >
        <div class="max-w-screen-xl mx-auto flex items-center justify-between gap-4">
          <div class="flex items-center gap-3">
            <div class="relative">
              <ImageWithFallback
                :src="selectedAgentId ? getAvatarUrl(agents.find(a => a.id === selectedAgentId)!) : getAvatarUrl(agents[0]!)"
                class="w-12 h-12 rounded-full border border-gray-100 object-cover"
              />
              <div class="absolute -bottom-1 -right-1 w-4 h-4 bg-green-500 border-2 border-white rounded-full"></div>
            </div>
            <div>
              <div class="text-xs text-gray-500 font-medium uppercase tracking-wider">
                {{ t('property.price') }}
              </div>
              <div class="text-lg font-bold text-brand leading-tight">
                {{ deal.price ? formatPrice(deal.price) : formatPrice(deal.rent) }}
              </div>
            </div>
          </div>
          <Button
            class="bg-brand text-white hover:bg-brand/90 px-8 py-3 rounded-xl font-bold shadow-lg shadow-brand/20"
            @click="scrollToContact"
          >
            {{ t('property.contact_agent') }}
          </Button>
        </div>
      </div>
    </Transition>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, markRaw, onMounted, unref } from 'vue';
import { navigate } from 'vike/client/router';
import { usePageContext } from "vike-vue/usePageContext";
import { useI18n } from '@repo/i18n';

import {
  ArrowLeft, DollarSign, Download, Train, GraduationCap, Building2, ExternalLink,
  HelpCircle,
} from 'lucide-vue-next';

import {
  PropertyDetailsTable,
  PropertyCard,
  useAddress,
  usePhotos,
  useCurrency,
  useSmartTitle,
  useMetricUnitStore,
  Breadcrumbs,
  InquiryForm,
  useNavStore
  } from '@repo/ui';
import ImageWithFallback from '@repo/ui/components/ImageWithFallback.vue';
import Button from '@repo/ui/components/Button.vue';
import Tooltip from '@repo/ui/components/Tooltip.vue';
import ImageGallery from '@repo/ui/components/property/ImageGallery.vue';
import PropertyHeader from '@repo/ui/components/property/PropertyHeader.vue';
import PropertySummary from '@repo/ui/components/property/PropertySummary.vue';
import PropertyFeatures from '@repo/ui/components/property/PropertyFeatures.vue';
import PropertyMap from '@repo/ui/components/property/PropertyMap.vue';

import { Neighborhood } from '@repo/types';
import type { PageContext } from 'vike/types'
import { PageDataBase } from '@repo/ui/hooks';
import type { Deal, AgentWithProfiles } from '@repo/types';

const pageContext = usePageContext() as PageContext<PageDataBase & { deal: Deal }>;
const { t, locale } = useI18n({});

const neighborhood = ref<Neighborhood | undefined>(pageContext.data.neighborhood); // Mark as possibly undefined

const { address, page } = useAddress()

const deal = computed(() => pageContext.data.deal)

const displayName = useSmartTitle(deal, locale, computed(() => page.value?.title));
const units = ref(markRaw(pageContext.data.units ?? []))
const otherUnits = computed(() => units.value.filter(unit => unit.id != deal.value?.id))
const { format: formatPrice } = useCurrency(pageContext.data.currencies);
const { images, onImageLoad } = usePhotos(deal, { priority: 'zumen' })
const navStore = useNavStore();

const agents = computed(() => (pageContext.data.agents as AgentWithProfiles[]) || [])
function toUrl(data: string): string {
  return 'data:image/png;base64,' + data
}

function getAvatarUrl(agent: AgentWithProfiles): string {
  if (agent.profile?.avatar?.url) return agent.profile.avatar.url;
  if (agent.profile?.avatar?.data) return 'data:image/png;base64,' + agent.profile.avatar.data;
  return '/images/avatar.svg';
}

const goBack = () => {
  if (window.history.length > 1) {
    window.history.back();
  } else {
    navigate('/' + pageContext.routeParams.path);
  }
};

const landPriceYearMap = {
  // S59-S63 (1984-1988)
  ...Object.fromEntries(Array.from({ length: 5 }, (_, i) => [`L01_${(62 + i).toString().padStart(3, '0')}`, 1984 + i])),
  // H1-H31 (1989-2019)
  ...Object.fromEntries(Array.from({ length: 31 }, (_, i) => [`L01_${(67 + i).toString().padStart(3, '0')}`, 1989 + i])),
  // R2-R5 (2020-2023)
  ...Object.fromEntries(Array.from({ length: 4 }, (_, i) => [`L01_${(98 + i).toString().padStart(3, '0')}`, 2020 + i])),
};


const showAllLandPriceHistory = ref(false);

const metricStore = useMetricUnitStore();
const displayedLandPriceHistory = computed(() => {
  if (showAllLandPriceHistory.value) {
    return landPriceHistory.value;
  }
  return landPriceHistory.value.slice(0, 3); // Show first 3 rows by default
});

const landPriceHistory = computed(() => {
  // Ensure neighborhood.value is not undefined before accessing its properties
  const mlitPrices = (neighborhood.value?.mlitLandPrices || []).flatMap(p =>
    Object.entries(landPriceYearMap).map(([key, year]) => {
      const price = p[key];
      if (price !== null && price !== undefined && price > 0) {
        return { year, price };
      }
      return null;
    }).filter(p => p !== null)
  );

  interface PricePoint {
    year: number
    price: number
  }

  type PriceHistory = Array<PricePoint>

  const allPrices: PriceHistory = [
    ...mlitPrices,
    ...(neighborhood.value?.prefecturalLandPrices || []).map(p => ({ year: p.L02_005, price: p.L02_006 }))
  ];

  if (allPrices.length === 0) return [];

  const pricesByYear = allPrices.reduce((acc, curr) => {
    if (!curr.year || !curr.price) return acc;
    if (!acc[curr.year]) {
      acc[curr.year] = [];
    }
    acc[curr.year].push(curr.price / (metricStore.target == 'm' ? 1 : metricStore.rate) ** 2);
    return acc;
  }, {} as Record<number, number[]>);

  const history = Object.keys(pricesByYear).map(year => {
    const prices = pricesByYear[parseInt(year)];
    const averagePrice = prices.reduce((a, b) => a + b, 0) / prices.length;
    return {
      year: parseInt(year),
      averagePrice: averagePrice,
      prices,
      dataPoints: prices.length
    };
  }).sort((a, b) => b.year - a.year);

  return history;
});

function averageCoordinates(points: Array<[number, number]>): [number, number] {
  return [
    points.map(coords => coords[0]).reduce((a, b) => a + b, 0) / points.length,
    points.map(coords => coords[1]).reduce((a, b) => a + b, 0) / points.length,
  ]
}

const schoolTypes = {
  '16001': t('enums.schoolType.elementary'),
  '16002': t('enums.schoolType.juniorHigh'),
  '16003': t('enums.schoolType.juniorHigh'),
  '16004': t('enums.schoolType.highSchool'),
  '16005': t('enums.schoolType.highSchool'),
  '16006': t('enums.schoolType.juniorCollege'),
  '16007': t('enums.schoolType.university'),
  '16011': t('enums.schoolType.kindergarten'),
  '16013': t('enums.schoolType.kindergarten'),
}

function getDistance(coords1: [number, number], coords2: [number, number]): number {
  const [lon1, lat1] = coords1;
  const [lon2, lat2] = coords2;
  const R = 6371e3; // metres
  const φ1 = lat1 * Math.PI/180; // φ, λ in radians
  const φ2 = lat2 * Math.PI/180;
  const Δφ = (lat2-lat1) * Math.PI/180;
  const Δλ = (lon2-lon1) * Math.PI/180;

  const a = Math.sin(Δφ/2) * Math.sin(Δφ/2) +
            Math.cos(φ1) * Math.cos(φ2) *
            Math.sin(Δλ/2) * Math.sin(Δλ/2);
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1-a));

  return R * c; // in metres
}

const nearbyStations = computed(() => {
  if (!neighborhood.value?.stations || !deal.value.building?.loc) return [];
  const propCoords = deal.value.building.loc.coordinates as [number, number];

  return (neighborhood.value.stations ?? [])
    .map(station => {
      const stationCoords = averageCoordinates(station.geometry.coordinates as [number, number][]);
      const distance = getDistance(propCoords, stationCoords);
      const walkTime = Math.round(distance / 80);

      return {
        name: station.station?.labels[unref(locale)] ?? station.N02_005,
        line: station.line?.labels[unref(locale)] ?? station.N02_003,
        walkTime: walkTime,
        distance: distance
      };
    })
    .sort((a, b) => a.distance - b.distance)
    .slice(0, 3);
});

const nearbySchools = computed(() => {
  if (!neighborhood.value?.schools || !deal.value?.building.loc) return [];
  const propCoords = deal.value.building.loc.coordinates as [number, number];

  return neighborhood.value.schools
    .filter(school => school.geometry.type === 'Point')
    .filter(school => ['16001', '16002', '16004', '16005'].includes(school.P29_003))
    .map(school => {
      const schoolCoords = school.geometry.coordinates as [number, number];
      const distance = getDistance(propCoords, schoolCoords);

      return {
        name: school.P29_004,
        type: schoolTypes[school.P29_003] || 'その他',
        distance: (distance / 1000).toFixed(1), // in km
        rawDistance: distance
      };
    })
    .sort((a, b) => a.rawDistance - b.rawDistance)
    .slice(0, 3);
});

const elementarySchoolZoneForProperty = computed(() => {
  return neighborhood.value?.elementarySchoolZones?.[0] ?? {}
});

const juniorHighSchoolZoneForProperty = computed(() => {
  return neighborhood.value?.juniorHighSchoolZones?.[0] ?? {}
});

import type { UrbanPlan } from '@repo/types'

const urbanPlanning = computed(() => {
  const plans = neighborhood.value?.urbanPlans ?? [];

  const getPlan = (filter: (p: UrbanPlan) => boolean) => plans.find(filter);

  return {
    usageZone: getPlan(p => Boolean(p.YoutoName || (p.AreaCode && p.AreaCode >= 1 && p.AreaCode <= 13))),
    cityPlanningArea: getPlan(p => Boolean(p.TokeiName || (p.AreaCode && p.AreaCode === 21))),
    areaClassification: getPlan(p => (p.AreaCode === 22 || p.AreaCode === 23)),
    locationOptimization: getPlan(p => (p.AreaCode === 31 || p.AreaCode === 32)),
    cityPlanningRoad: getPlan(p => Boolean(p.DouroType || (p.AreaCode && p.AreaCode >= 3000))),
  };
});

function getGoogleMapsSearchUrlByName(name: string) {
  if (!name) return '';
  return `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(name)}`;
}

const mlitDataSourceUrls = {
  stations: 'https://nlftp.mlit.go.jp/ksj/gml/datalist/KsjTmplt-N02-v3_1.html',
  schools: 'https://nlftp.mlit.go.jp/ksj/gml/datalist/KsjTmplt-P29-v2_0.html',
  elementarySchoolZones: 'https://nlftp.mlit.go.jp/ksj/gml/datalist/KsjTmplt-A27-v3_0.html',
  juniorHighSchoolZones: 'https://nlftp.mlit.go.jp/ksj/gml/datalist/KsjTmplt-A32-v3_0.html',
  urbanPlans: 'https://nlftp.mlit.go.jp/ksj/gml/datalist/KsjTmplt-A55-2024.html',
  landPriceHistory: 'https://nlftp.mlit.go.jp/ksj/gml/datalist/KsjTmplt-L01-v3_1.html',
};

// inquiry
const contactSection = ref<HTMLElement | null>(null);
const isContactVisible = ref(true);

onMounted(() => {
  const observer = new IntersectionObserver(
    ([entry]) => {
      isContactVisible.value = entry.isIntersecting;
    },
    { threshold: 0.1 }
  );

  if (contactSection.value) {
    observer.observe(contactSection.value);
  }
});

const scrollToContact = () => {
  contactSection.value?.scrollIntoView({ behavior: 'smooth' });
};

const nearbyProperties = computed(() => pageContext.data.map?.buildings?.filter(b => b.deals.every(d => d.id !== deal.value?.id)) ?? [])
</script>
